/**
 * The pure side of the sidebar's scope switcher: what the header block names for the caller's
 * position, and which rows its menu offers. Takes a structural view of `me.tenants`, so a test
 * builds one from a few literals.
 */

/** A tenant as `me.tenants` returns it. */
export interface ScopeTenant {
  id: string;
  name: string;
  slug: string;
  archivedAt: Date | string | null;
}

export interface ScopeInput {
  isPlatformAdmin: boolean;
  tenants: readonly ScopeTenant[];
  /** The route's tenant, or null outside one. */
  tenantId: string | null;
  /** The last tenant this browser visited, or null (`scopeStore.lastTenantId`). */
  lastTenantId: string | null;
}

/** What the header block shows: a name and, below it, the tier it belongs to. */
export interface ScopeIdentity {
  /** "Loot OS" outside a tenant; the tenant's name inside one, "…" until `me.tenants` resolves. */
  name: string;
  /** "Platform", "Tenant" or "Archived tenant"; null for a member outside any tenant, who is in neither scope. */
  tier: string | null;
  /** Whether the block stands for a tenant, whose mark is its initials rather than the logo. */
  tenant: boolean;
}

export type ScopeRow =
  | { kind: "platform"; current: boolean }
  | { kind: "tenant"; tenant: ScopeTenant; current: boolean }
  | { kind: "search" };

export function scopeIdentity(
  input: Pick<ScopeInput, "isPlatformAdmin" | "tenants" | "tenantId">,
): ScopeIdentity {
  if (input.tenantId) {
    const tenant = input.tenants.find((t) => t.id === input.tenantId);
    return {
      name: tenant?.name ?? "…",
      tier: tenant?.archivedAt ? "Archived tenant" : "Tenant",
      tenant: true,
    };
  }
  return { name: "Loot OS", tier: input.isPlatformAdmin ? "Platform" : null, tenant: false };
}

/**
 * The menu's rows. A platform admin gets "Platform", one tenant shortcut (the current one, else
 * the last one visited) and the palette, never the whole tenant list: `me.tenants` is every
 * tenant for an admin, archived ones included. A member gets their tenants, the current one
 * first; `me.tenants` already hides an archived tenant from them.
 */
export function scopeRows(input: ScopeInput): ScopeRow[] {
  const { isPlatformAdmin, tenants, tenantId, lastTenantId } = input;
  const current = tenantId ? (tenants.find((t) => t.id === tenantId) ?? null) : null;
  if (isPlatformAdmin) {
    const shortcut =
      current ?? (lastTenantId ? (tenants.find((t) => t.id === lastTenantId) ?? null) : null);
    return [
      { kind: "platform", current: tenantId === null },
      ...(shortcut
        ? [{ kind: "tenant" as const, tenant: shortcut, current: shortcut.id === tenantId }]
        : []),
      { kind: "search" },
    ];
  }
  const others = tenants.filter((t) => t.id !== tenantId);
  return [...(current ? [current] : []), ...others].map((tenant) => ({
    kind: "tenant",
    tenant,
    current: tenant.id === tenantId,
  }));
}

/** Whether any row leads somewhere else; the menu lists none for a member inside their one tenant. */
export function canSwitchScope(rows: readonly ScopeRow[]): boolean {
  return rows.some((row) => row.kind === "search" || !row.current);
}
